import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MembershipService } from '../../../core/services/membership.service';
import { ToastService } from '../../../core/services/toast.service';
import { AdminMemberView, RoleHelper, PendingInvitation } from '../../../core/models/membership.model';
import { EditMemberRoleModalComponent } from '../../../shared/components/edit-member-role-modal.component';
import { AssignOwnerModalComponent } from '../../../shared/components/assign-owner-modal.component';
import { AssignChiefSupervisorModalComponent } from '../../../shared/components/assign-chief-supervisor-modal.component';
import { AdminInviteMemberModalComponent } from '../../../shared/components/admin-invite-member-modal.component';

/**
 * Organization Members Tab Component (organization-detail path)
 *
 * Displays members list with role management options for admins.
 * Supports:
 * - Viewing members list with roles
 * - Editing member roles (Pilot/PIC)
 * - Transferring ownership
 * - Assigning Chief Supervisor
 * - Removing/reactivating members
 */
@Component({
  selector: 'app-organization-members-tab',
  standalone: true,
  imports: [
    CommonModule,
    EditMemberRoleModalComponent,
    AssignOwnerModalComponent,
    AssignChiefSupervisorModalComponent,
    AdminInviteMemberModalComponent
  ],
  templateUrl: './organization-members-tab.component.html',
  styleUrl: './organization-members-tab.component.scss'
})
export class OrganizationMembersTabComponent implements OnInit, OnChanges {
  @Input() organizationId!: string;
  @Input() organizationName: string = '';
  @Input() members: AdminMemberView[] = [];
  @Input() ownerProfileId!: string;
  @Input() chiefSupervisorProfileId?: string;
  @Output() membersUpdated = new EventEmitter<void>();

  showEditRoleModal = false;
  showAssignOwnerModal = false;
  showAssignCSModal = false;
  showInviteMemberModal = false;
  selectedMember: AdminMemberView | null = null;
  errorMessage = '';
  processing = false;

  pendingInvitations: PendingInvitation[] = [];
  loadingInvitations = false;

  readonly RoleHelper = RoleHelper;

  constructor(
    private membershipService: MembershipService,
    private toastService: ToastService,
    private router: Router
  ) {}

  viewMember(profileId: string): void {
    this.router.navigate(['/users', profileId], {
      queryParams: { returnUrl: this.router.url, returnLabel: `${this.organizationName}` }
    });
  }

  ngOnInit(): void {
    this.loadPendingInvitations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      this.loadPendingInvitations();
    }
  }

  /**
   * Load pending invitations
   */
  async loadPendingInvitations(): Promise<void> {
    if (!this.organizationId) return;

    this.loadingInvitations = true;
    try {
      const result = await this.membershipService.getPendingInvitations(this.organizationId);
      this.pendingInvitations = result.invitations;
    } catch (error) {
      console.error('Error loading invitations:', error);
    } finally {
      this.loadingInvitations = false;
    }
  }

  /**
   * Open edit role modal
   */
  openEditRoleModal(member: AdminMemberView): void {
    this.selectedMember = member;
    this.showEditRoleModal = true;
  }

  /**
   * Open assign owner modal
   */
  openAssignOwnerModal(): void {
    this.showAssignOwnerModal = true;
  }

  /**
   * Open assign chief supervisor modal
   */
  openAssignCSModal(): void {
    this.showAssignCSModal = true;
  }

  /**
   * Open invite member modal
   */
  openInviteMemberModal(): void {
    this.showInviteMemberModal = true;
  }

  /**
   * Close edit role modal
   */
  closeEditRoleModal(): void {
    this.showEditRoleModal = false;
    this.selectedMember = null;
  }

  /**
   * Close assign owner modal
   */
  closeAssignOwnerModal(): void {
    this.showAssignOwnerModal = false;
  }

  /**
   * Close assign CS modal
   */
  closeAssignCSModal(): void {
    this.showAssignCSModal = false;
  }

  /**
   * Close invite member modal
   */
  closeInviteMemberModal(): void {
    this.showInviteMemberModal = false;
  }

  /**
   * Handle role updated
   */
  onRoleUpdated(): void {
    this.toastService.success('Member role updated successfully');
    this.membersUpdated.emit();
  }

  /**
   * Handle ownership transferred
   */
  onOwnershipTransferred(): void {
    this.toastService.success('Ownership transferred successfully');
    this.membersUpdated.emit();
  }

  /**
   * Handle chief supervisor assigned
   */
  onChiefSupervisorAssigned(): void {
    this.toastService.success('Chief Supervisor updated successfully');
    this.membersUpdated.emit();
  }

  /**
   * Handle member invited
   */
  onMemberInvited(): void {
    this.toastService.success('Member invitation sent successfully');
    this.closeInviteMemberModal();
    this.loadPendingInvitations();
    this.membersUpdated.emit();
  }

  /**
   * Cancel invitation
   */
  async cancelInvitation(invitation: PendingInvitation): Promise<void> {
    if (!confirm(`Are you sure you want to cancel the invitation for ${invitation.email}?`)) {
      return;
    }

    this.processing = true;
    this.errorMessage = '';

    try {
      await this.membershipService.cancelInvitation(this.organizationId, invitation.id);
      this.toastService.success(`Invitation for ${invitation.email} cancelled successfully`);
      this.loadPendingInvitations();
    } catch (error: any) {
      console.error('Failed to cancel invitation:', error);
      this.toastService.error(error.message || 'Failed to cancel invitation');
    } finally {
      this.processing = false;
    }
  }

  /**
   * Send invitation email (for invitations that haven't been sent yet)
   * Updates local state immediately after successful API call.
   */
  async sendInvitationEmail(invitation: PendingInvitation): Promise<void> {
    this.processing = true;
    this.errorMessage = '';

    try {
      const result = await this.membershipService.sendInvitationEmail(invitation.id);

      // Update local invitation state immediately from API response
      invitation.emailStatus = result.emailStatus;
      invitation.emailSentAt = result.emailSentAt;
      invitation.resendCount = result.resendCount;

      this.toastService.success('Invitation email sent successfully');
    } catch (error: any) {
      console.error('Failed to send invitation email:', error);

      // Update local status to reflect failure
      invitation.emailStatus = 'failed';

      this.toastService.error(error.message || 'Failed to send invitation email');
    } finally {
      this.processing = false;
    }
  }

  /**
   * Resend invitation
   */
  async resendInvitation(invitation: PendingInvitation): Promise<void> {
    this.processing = true;
    this.errorMessage = '';

    try {
      await this.membershipService.resendInvitation(this.organizationId, invitation.id);
      this.toastService.success(`Invitation resent to ${invitation.email}`);
      this.loadPendingInvitations();
    } catch (error: any) {
      console.error('Failed to resend invitation:', error);
      this.toastService.error(error.message || 'Failed to resend invitation');
    } finally {
      this.processing = false;
    }
  }

  /**
   * Get email status badge class
   */
  getEmailStatusClass(status: string): string {
    switch (status) {
      case 'sent':
        return 'bg-success';
      case 'failed':
        return 'bg-danger';
      case 'never_sent':
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get email status display text
   */
  getEmailStatusText(status: string): string {
    switch (status) {
      case 'sent':
        return 'Email Sent';
      case 'failed':
        return 'Email Failed';
      case 'never_sent':
      default:
        return 'Not Sent';
    }
  }

  /**
   * Remove member
   */
  async removeMember(member: AdminMemberView): Promise<void> {
    if (!confirm(`Are you sure you want to remove ${member.firstName} ${member.lastName} from this organization?`)) {
      return;
    }

    this.processing = true;
    this.errorMessage = '';

    try {
      await this.membershipService.removeMember(this.organizationId, member.profileId);
      this.toastService.success(`${member.firstName} ${member.lastName} removed successfully`);
      this.membersUpdated.emit();
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      this.toastService.error(error.message || 'Failed to remove member');
    } finally {
      this.processing = false;
    }
  }

  /**
   * Reactivate member
   */
  async reactivateMember(member: AdminMemberView): Promise<void> {
    this.processing = true;
    this.errorMessage = '';

    try {
      await this.membershipService.reactivateMember(this.organizationId, member.profileId);
      this.toastService.success(`${member.firstName} ${member.lastName} reactivated successfully`);
      this.membersUpdated.emit();
    } catch (error: any) {
      console.error('Failed to reactivate member:', error);
      this.toastService.error(error.message || 'Failed to reactivate member');
    } finally {
      this.processing = false;
    }
  }

  /**
   * Get active members
   */
  get activeMembers(): AdminMemberView[] {
    return this.members.filter(m => m.isActive);
  }

  /**
   * Get inactive members
   */
  get inactiveMembers(): AdminMemberView[] {
    return this.members.filter(m => !m.isActive);
  }

  get pendingMembers(): AdminMemberView[] {
    return this.members.filter(m => m.isActive && m.hasAcceptedInvite === false);
  }

  /**
   * Get membership role names (Pilot/PIC only, excluding Owner/CS)
   */
  getMembershipRoleNames(role: number): string[] {
    return RoleHelper.getRoleNames(role).filter(
      r => r !== 'Owner' && r !== 'Chief Supervisor'
    );
  }

  /**
   * Format date
   */
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Check if a member already has a pending invitation by email
   */
  hasPendingInvitation(email: string): boolean {
    if (!email) return false;
    return this.pendingInvitations.some(
      inv => inv.email?.toLowerCase() === email.toLowerCase()
    );
  }

  getPendingInvitation(email: string): PendingInvitation | null {
    return this.pendingInvitations.find(
      inv => inv.email?.toLowerCase() === email.toLowerCase()
    ) ?? null;
  }

  /**
   * Send an invitation for a member who doesn't have a pending invitation
   */
  async sendInviteForMember(member: AdminMemberView): Promise<void> {
    this.processing = true;
    this.errorMessage = '';

    try {
      await this.membershipService.inviteMember(this.organizationId, {
        email: member.email,
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        role: member.role || 1, // Default to Pilot if no role set
      });

      this.toastService.success(`Invitation sent to ${member.email}`);
      this.loadPendingInvitations();
    } catch (error: any) {
      console.error('Failed to send invitation:', error);
      this.toastService.error(error.message || 'Failed to send invitation');
    } finally {
      this.processing = false;
    }
  }

  /**
   * Check if invitation is expiring soon (within 2 days)
   */
  isExpiringSoon(expiresAt: string): boolean {
    if (!expiresAt) return false;
    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const twoDaysFromNow = new Date(now.getTime() + (2 * 24 * 60 * 60 * 1000));
    return expiryDate <= twoDaysFromNow && expiryDate > now;
  }
}
